import type { Analysis } from '@fishymeter/types';

export interface ContentPreviewProps {
  analysis: Analysis;
}

/**
 * Compact "what was scored" panel shown above the score on result pages.
 * Per-input preview shape:
 *   - image: thumbnail (max 200px tall) using a presigned URL from the API
 *   - pdf:   filename + extracted-text excerpt
 *   - text:  excerpt of the pasted text
 *   - url:   the source URL as a clickable link
 * When the underlying S3 object has expired (30-day retention), surfaces a
 * placeholder instead of the thumbnail.
 */
export function ContentPreview({ analysis }: ContentPreviewProps) {
  const {
    input_type,
    source_url,
    source_text_excerpt,
    source_image_url,
    source_filename,
    source_expired,
  } = analysis;

  const wrap = (children: React.ReactNode) => (
    <section className="rounded-lg border border-border bg-muted/30 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Content preview
      </p>
      <div className="mt-3">{children}</div>
    </section>
  );

  if (source_expired) {
    return wrap(
      <p className="text-sm italic text-muted-foreground">
        Original no longer available (30-day retention).
      </p>,
    );
  }

  if (input_type === 'url' && source_url) {
    return wrap(
      <a
        href={source_url}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-sm text-primary underline-offset-2 hover:underline"
      >
        {source_url}
      </a>,
    );
  }

  if (input_type === 'image' && source_image_url) {
    return wrap(
      <img
        src={source_image_url}
        alt={source_filename ?? 'Submitted image'}
        className="mx-auto max-h-[200px] w-auto rounded-md border border-border"
      />,
    );
  }

  if (input_type === 'pdf') {
    return wrap(
      <div className="space-y-2 text-sm">
        {source_filename && (
          <p className="font-medium text-foreground">{source_filename}</p>
        )}
        {source_text_excerpt ? (
          <p className="text-muted-foreground">{source_text_excerpt}</p>
        ) : (
          <p className="italic text-muted-foreground">PDF document.</p>
        )}
      </div>,
    );
  }

  if (input_type === 'text' && source_text_excerpt) {
    return wrap(
      <p className="text-sm text-muted-foreground">{source_text_excerpt}</p>,
    );
  }

  // No preview available (legacy row, missing fields, etc.). Render nothing.
  return null;
}
